import { useRef } from "react";

// Long-press to lift a row, then drag to reorder — pointer events only, no library.
//  • Before the lift fires, moving past `threshold` px cancels it → native scroll wins.
//  • On lift: haptics + a floating clone (pointer-events:none) follows the finger; the
//    caller renders the placeholder gap and commits on drop. During the drag we listen on
//    `window` (not the row) so a re-render/reorder of the list can't drop the gesture.
//  • Autoscrolls when the pointer nears a viewport edge; Escape or drop-outside cancels.
//
// The caller wires the data model via callbacks (all optional):
//   canLift(el) -> boolean   gate the lift (e.g. a create form is open)
//   onLift(el)               side effects on lift (e.g. ensure edit mode is on, mark source)
//   onHover(x, y)            compute + set the placeholder target
//   onDrop()                 commit the reorder for the current target
//   onCancel()               clear the placeholder, no change
// Callbacks are read from a ref, so the window listeners stay stable across re-renders
// (the list re-renders on every hover as the placeholder moves).
//
// Returns { handlers } to spread on every draggable row, and `fired` (true right after a
// long-press) so the row's onClick can swallow the trailing tap.
export function useDragReorder(opts = {}) {
  const cb = useRef(opts);
  cb.current = opts;                       // always the latest callbacks + config

  const timer = useRef(null);
  const startPt = useRef(null);
  const fired = useRef(false);
  const dragging = useRef(false);
  const ghost = useRef(null);
  const raf = useRef(null);
  const lastXY = useRef({ x: 0, y: 0 });

  // Stable machinery — created once, closes over the refs above (never over stale props).
  const S = useRef(null);
  if (!S.current) {
    const moveGhost = (x, y) => {
      const g = ghost.current;
      if (!g) return;
      g.style.left = (x - g._dx) + "px";
      g.style.top = (y - g._dy) + "px";
    };
    const removeGhost = () => { if (ghost.current) { ghost.current.remove(); ghost.current = null; } };
    const makeGhost = (el, x, y) => {
      const r = el.getBoundingClientRect();
      const g = el.cloneNode(true);
      g.classList.add("bz-drag-ghost");
      if (cb.current.ghostClass) g.classList.add(cb.current.ghostClass);
      g.style.position = "fixed";
      g.style.left = r.left + "px";
      g.style.top = r.top + "px";
      g.style.width = r.width + "px";
      g.style.height = r.height + "px";
      g.style.margin = "0";
      g.style.pointerEvents = "none";
      g._dx = x - r.left;
      g._dy = y - r.top;
      ghost.current = g;
      document.body.appendChild(g);
      moveGhost(x, y);
    };
    const autoscroll = () => {
      raf.current = requestAnimationFrame(autoscroll);
      const y = lastXY.current.y, h = window.innerHeight, edge = 72;
      let dy = 0;
      if (y < edge) dy = -Math.ceil((edge - y) / 5);
      else if (y > h - edge) dy = Math.ceil((y - (h - edge)) / 5);
      if (dy) {
        window.scrollBy(0, dy);
        // prst stojí, ale obsah sa posúva → prepočítaj cieľ, nech placeholder ide s obsahom
        if (cb.current.onHover) cb.current.onHover(lastXY.current.x, y);
      }
    };
    const onWinMove = (e) => {
      lastXY.current = { x: e.clientX, y: e.clientY };
      moveGhost(e.clientX, e.clientY);
      if (cb.current.onHover) cb.current.onHover(e.clientX, e.clientY);
    };
    // KRITICKÉ pre dotyk: preventDefault na pointermove NEzastaví scroll — musíme
    // preventnúť touchmove (non-passive). Bez toho sa počas ťahu scrolluje obrazovka.
    const onWinTouchMove = (e) => { if (e.cancelable) e.preventDefault(); };
    const teardown = (commit) => {
      if (!dragging.current) return;
      dragging.current = false;
      if (raf.current) { cancelAnimationFrame(raf.current); raf.current = null; }
      removeGhost();
      document.body.classList.remove("bz-dragging");
      window.removeEventListener("pointermove", onWinMove);
      window.removeEventListener("touchmove", onWinTouchMove);
      window.removeEventListener("pointerup", onWinUp);
      window.removeEventListener("pointercancel", onWinCancel);
      window.removeEventListener("keydown", onKey);
      if (commit) { if (cb.current.onDrop) cb.current.onDrop(); }
      else if (cb.current.onCancel) cb.current.onCancel();
    };
    const onWinUp = () => teardown(true);
    const onWinCancel = () => teardown(false);
    const onKey = (e) => { if (e.key === "Escape") teardown(false); };
    const lift = (el, x, y, pointerId) => {
      if (cb.current.canLift && !cb.current.canLift(el)) return;
      fired.current = true;
      dragging.current = true;
      try { if (pointerId != null) el.setPointerCapture(pointerId); } catch { /* capture je best-effort */ }
      try { if (navigator.vibrate) navigator.vibrate(15); } catch { /* best-effort */ }
      if (cb.current.onLift) cb.current.onLift(el);
      makeGhost(el, x, y);
      document.body.classList.add("bz-dragging");
      lastXY.current = { x, y };
      if (cb.current.onHover) cb.current.onHover(x, y);
      raf.current = requestAnimationFrame(autoscroll);
      window.addEventListener("pointermove", onWinMove);
      window.addEventListener("touchmove", onWinTouchMove, { passive: false });   // blokuje scroll počas ťahu
      window.addEventListener("pointerup", onWinUp);
      window.addEventListener("pointercancel", onWinCancel);
      window.addEventListener("keydown", onKey);
    };
    S.current = { lift, dragging };
  }

  const clearTimer = () => { if (timer.current) { clearTimeout(timer.current); timer.current = null; } };

  const handlers = {
    onPointerDown: (e) => {
      // don't start the long-press if the press began on an INNER control (check, chevron…)
      // — but do allow it when currentTarget is itself the button/row, or a plain div header.
      const btn = e.target.closest("button, a, input, [role=button]");
      if (btn && btn !== e.currentTarget && e.currentTarget.contains(btn)) return;
      fired.current = false;
      const el = e.currentTarget;
      const pid = e.pointerId;
      startPt.current = { x: e.clientX, y: e.clientY };
      clearTimer();
      timer.current = setTimeout(() => {
        timer.current = null;
        const p = startPt.current;
        S.current.lift(el, p.x, p.y, pid);   // finger is still ~stationary (movement would have cancelled)
      }, cb.current.longPressMs ?? 450);
    },
    onPointerMove: (e) => {
      if (dragging.current || !timer.current || !startPt.current) return;
      const th = cb.current.threshold ?? 12;
      const dx = e.clientX - startPt.current.x, dy = e.clientY - startPt.current.y;
      if (dx * dx + dy * dy > th * th) {
        clearTimer();
        // Myš nemá scroll-konflikt (scrolluje sa kolieskom, nie ťahom): pohyb pred
        // uplynutím long-pressu ťah rovno ZAČNE. Dotyk/pero ostáva „pohyb = scroll".
        if (e.pointerType === "mouse" && (e.buttons & 1)) {
          S.current.lift(e.currentTarget, e.clientX, e.clientY, e.pointerId);
        }
      }
    },
    onPointerUp: () => clearTimer(),
    onPointerLeave: (e) => {
      // rýchly myší flick môže opustiť riadok skôr, než move prekročí prah — aj to je ťah
      const pending = !dragging.current && timer.current != null;
      clearTimer();
      if (pending && e.pointerType === "mouse" && (e.buttons & 1)) {
        S.current.lift(e.currentTarget, e.clientX, e.clientY, e.pointerId);
      }
    },
    onPointerCancel: () => clearTimer(),
    onContextMenu: (e) => e.preventDefault(),
  };

  // Drag HANDLE — immediate drag (no long-press, no scroll conflict): the handle sets
  // touch-action:none so a touch never scrolls, and the drag starts on pointerdown. The
  // handle grabs the whole ITEM (via `itemSelector`), not just the handle, so the ghost is
  // the full row/block. stopPropagation keeps the row's long-press from also firing.
  const handleProps = {
    onPointerDown: (e) => {
      if (e.button != null && e.button > 0) return;   // left button / touch only
      e.stopPropagation();
      const sel = cb.current.itemSelector;
      const item = sel ? e.currentTarget.closest(sel) : e.currentTarget;
      if (!item) return;
      S.current.lift(item, e.clientX, e.clientY, e.pointerId);
      fired.current = false;   // handle press produces no row-click to swallow
    },
    onContextMenu: (e) => e.preventDefault(),
    style: { touchAction: "none" },
  };

  return { handlers, handleProps, fired };
}
